import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Snackbar } from '@mui/material';
import moment from 'moment';
import EventBlockDrawer from '../inventory/EventBlockDrawer';
import { normalizeRoomTypeId } from '../inventory/EventBlockAdjustView';
import {
  fetchActiveTodayInventoryBlocks,
  createInventoryBlock,
  updateInventoryBlock,
  setInventoryBlockActive,
  getInventoryBlockErrorMessage,
  parseInventoryBlockTime,
  formatInventoryBlockTime,
} from '../../services/inventoryBlockService';
import { getConfigurationRoomTypes, getChannels, getInventoryData } from '../../services/api';
import {
  DEFAULT_RATE_CHANNEL_SELECTION,
  deriveBlockChannelSelection,
  expandChannelSelection,
} from '../common/AllChannelsSelector';

const EMPTY_FORM = {
  room_type_id: '',
  block_type: 'MANUAL',
  start_date: null,
  end_date: null,
  start_time: '',
  end_time: '',
  num_rooms: 1,
  reason: '',
  is_active: true,
  selectedChannels: [...DEFAULT_RATE_CHANNEL_SELECTION],
  selected_room_ids: [],
};

const DashboardEventBlockDrawer = ({ open, context, onClose, onSuccess }) => {
  const [blocks, setBlocks] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [blocksData, roomTypesData] = await Promise.all([
        fetchActiveTodayInventoryBlocks(),
        getConfigurationRoomTypes(),
      ]);
      setBlocks(blocksData.blocks || []);
      setRoomTypes(roomTypesData || []);
    } catch {
      setSnackbar({
        open: true,
        message: 'Failed to load inventory blocks. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchChannels = useCallback(async () => {
    try {
      const channelsData = await getChannels();
      const channelsArray = channelsData?.channels || (Array.isArray(channelsData) ? channelsData : []);
      setChannels(channelsArray);
    } catch {
      setChannels([]);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    fetchData();
    fetchChannels();
  }, [open, fetchData, fetchChannels]);

  useEffect(() => {
    if (!open || !context?.roomTypeId) return;

    const start = context.startDate ? moment(context.startDate) : moment();
    const end = start.clone().add(1, 'day');
    const matchedRoomType = roomTypes.find(
      (roomType) => String(roomType.id) === String(context.roomTypeId)
    );

    setEditingBlock(null);
    setErrors({});
    setErrorMessage(null);
    setFormData({
      ...EMPTY_FORM,
      room_type_id: normalizeRoomTypeId(matchedRoomType?.id ?? context.roomTypeId),
      start_date: start,
      end_date: end,
      selected_room_ids: context.roomId ? [Number(context.roomId)].filter(Boolean) : [],
      num_rooms: context.roomId ? 1 : 1,
    });
  }, [open, context?.roomTypeId, context?.startDate, context?.roomId, roomTypes]);

  const handleCloseDrawer = () => {
    onClose?.();
    setEditingBlock(null);
    setFormData(EMPTY_FORM);
    setErrors({});
    setErrorMessage(null);
  };

  const handleOpenEdit = (block) => {
    if (!block) return;

    setEditingBlock(block);
    setFormData({
      room_type_id: block.room_type_id || '',
      block_type: block.block_type || 'MANUAL',
      start_date: block.start_date ? moment(block.start_date) : null,
      end_date: block.end_date ? moment(block.end_date) : null,
      start_time: parseInventoryBlockTime(block.start_time) || '',
      end_time: parseInventoryBlockTime(block.end_time) || '',
      num_rooms: block.num_rooms || 1,
      reason: block.reason || '',
      is_active: block.is_active !== undefined ? block.is_active : true,
      selectedChannels: deriveBlockChannelSelection(block),
      selected_room_ids: Array.isArray(block.room_ids)
        ? block.room_ids.map((id) => Number(id)).filter(Boolean)
        : [],
    });
    setErrors({});
    setErrorMessage(null);
  };

  const handleSave = async () => {
    const newErrors = {};
    if (!formData.room_type_id) {
      newErrors.room_type_id = 'Room type is required';
    }
    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }
    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    }
    if (formData.start_date && formData.end_date && formData.end_date.isBefore(formData.start_date, 'day')) {
      newErrors.end_date = 'End date must be after or equal to start date';
    }
    const blockStartTime = parseInventoryBlockTime(formData.start_time);
    const blockEndTime = parseInventoryBlockTime(formData.end_time);
    if (!blockStartTime) {
      newErrors.start_time = 'Block time is required';
    }
    if (!blockEndTime) {
      newErrors.end_time = 'Unblock time is required';
    }
    if (
      blockStartTime &&
      blockEndTime &&
      formData.start_date &&
      formData.end_date &&
      formData.end_date.isSame(formData.start_date, 'day') &&
      !blockEndTime.isAfter(blockStartTime)
    ) {
      newErrors.end_time = 'Unblock time must be after block time on the same day';
    }
    if (!formData.selectedChannels || formData.selectedChannels.length === 0) {
      newErrors.selectedChannels = 'At least one channel must be selected';
    }
    const selectedRoomIds = Array.isArray(formData.selected_room_ids)
      ? formData.selected_room_ids.map((id) => Number(id)).filter(Boolean)
      : [];
    if (selectedRoomIds.length === 0) {
      newErrors.selected_room_ids = 'Select at least one room to block';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);
    try {
      const expanded = expandChannelSelection(formData.selectedChannels || [], channels);
      const channelIds = expanded
        .filter((id) => id !== 'pms')
        .map((id) => parseInt(id, 10))
        .filter((id) => !Number.isNaN(id));

      const dataToSend = {
        room_type_id: formData.room_type_id,
        block_type: formData.block_type,
        start_date: formData.start_date ? formData.start_date.format('YYYY-MM-DD') : null,
        end_date: formData.end_date ? formData.end_date.format('YYYY-MM-DD') : null,
        start_time: formatInventoryBlockTime(formData.start_time),
        end_time: formatInventoryBlockTime(formData.end_time),
        num_rooms: selectedRoomIds.length,
        reason: formData.reason || null,
        is_active: formData.is_active,
        channel_ids: channelIds,
        include_pms: (formData.selectedChannels || []).includes('pms'),
        room_ids: selectedRoomIds,
      };

      if (editingBlock) {
        await updateInventoryBlock(editingBlock.id, dataToSend);
        setSnackbar({
          open: true,
          message: 'Inventory block updated successfully',
          severity: 'success',
        });
      } else {
        await createInventoryBlock(dataToSend);
        setSnackbar({
          open: true,
          message: 'Inventory block created successfully',
          severity: 'success',
        });
      }

      handleCloseDrawer();
      onSuccess?.();
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrorMessage(error.response?.data?.error || 'Failed to save inventory block');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (block) => {
    if (!block?.id) return;
    setIsSaving(true);
    try {
      const nextActive = !block.is_active;
      await setInventoryBlockActive(block, nextActive);
      setSnackbar({
        open: true,
        message: nextActive
          ? 'Inventory block enabled — inventory recalculated'
          : 'Inventory block disabled — inventory recalculated',
        severity: 'success',
      });
      await fetchData();
      onSuccess?.();
    } catch (error) {
      setSnackbar({
        open: true,
        message: getInventoryBlockErrorMessage(error, 'Failed to update inventory block status'),
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <EventBlockDrawer
        open={open}
        onClose={handleCloseDrawer}
        onSave={handleSave}
        isSaving={isSaving}
        errorMessage={errorMessage}
        onErrorMessageChange={setErrorMessage}
        inventoryBlocks={blocks}
        loading={loading}
        onRefresh={fetchData}
        onEdit={handleOpenEdit}
        onToggleActive={handleToggleActive}
        formData={formData}
        onFormDataChange={setFormData}
        errors={errors}
        onErrorsChange={setErrors}
        allRoomTypes={roomTypes}
        channels={channels}
        getInventoryData={getInventoryData}
        editingBlock={editingBlock}
        initialMode={context?.manageTab ? 'manage' : 'add'}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DashboardEventBlockDrawer;
